import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";

import { Footer } from "@/components/Footer";
import { Masthead } from "@/components/Masthead";
import { Stat } from "@/components/Stat";
import { Tetromino } from "@/components/Tetromino";
import { pieceColorVar } from "@/game/pieces";
import { api } from "@/lib/api";
import { formatAge, formatInteger, formatStat } from "@/lib/format";
import { ApiError } from "@/lib/httpClient";
import { avatarUrl, playersApi, type PlayerPage } from "@/lib/playersApi";
import { rankInk } from "@/lib/rankInk";
import { useApi } from "@/lib/useApi";

function Shell({ children }: { readonly children: ReactNode }) {
  const club = useApi((signal) => api.club(signal));
  return (
    <>
      <Masthead variant="page" club={club.data} />
      <main className="lattice min-h-[calc(100vh-64px)] border-b-2 border-ink">
        <div className="shell py-10 md:py-14">{children}</div>
      </main>
      <Footer club={club.data} />
    </>
  );
}

/** The picture, their favourite piece, or their rank badge — in that order. */
function Portrait({ page }: { readonly page: PlayerPage }) {
  const { player, profile } = page;
  const rankTint = rankInk(player.current_rank);
  const tint = profile?.favourite_piece
    ? pieceColorVar(profile.favourite_piece)
    : "var(--color-ink)";

  return (
    <div
      className="slab flex h-44 w-44 shrink-0 items-center justify-center overflow-hidden p-0"
      style={{ ["--slab-ink" as string]: tint }}
    >
      {profile?.has_avatar ? (
        <img
          src={avatarUrl(player.username)}
          alt=""
          width={176}
          height={176}
          className="h-full w-full object-cover"
        />
      ) : profile?.favourite_piece ? (
        <Tetromino type={profile.favourite_piece} cell={22} />
      ) : (
        // No picture and no chosen piece: fall back to their rank badge, in the
        // same ink the roster table gives it.
        <span
          className="flex h-full w-full items-center justify-center font-mono text-5xl leading-none"
          style={{
            background: rankTint.background,
            color: rankTint.foreground,
          }}
        >
          {player.current_rank ? player.current_rank.toUpperCase() : "—"}
        </span>
      )}
    </div>
  );
}

/**
 * The standings, taken from the same cache the roster table reads.
 *
 * Shown for every player, written profile or not: the numbers are the reason
 * most people click a name, and they are already loaded.
 */
function Standings({ page }: { readonly page: PlayerPage }) {
  const { player, meta } = page;

  return (
    <section className="mt-12">
      <div className="mb-5 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h2 className="text-2xl">TETR.IO</h2>
        <span className="meta opacity-55">
          {player.live
            ? `updated ${formatAge(meta.age_seconds)}`
            : "last known standings"}
        </span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Club rank" value={`#${player.rank}`} size="panel" />
        <Stat
          label="Tetra League"
          value={player.current_rank ? player.current_rank.toUpperCase() : "—"}
          note={
            player.peak_rank_label !== "—"
              ? `peak ${player.peak_rank_label}`
              : undefined
          }
          size="panel"
        />
        <Stat label="TR" value={formatInteger(player.tr)} size="panel" />
        <Stat
          label="Games"
          value={formatInteger(player.games_played)}
          note={player.games_played > 0 ? `${player.games_won} won` : undefined}
          size="panel"
        />
        <Stat label="PPS" value={formatStat(player.pps)} size="panel" />
        <Stat label="APM" value={formatStat(player.apm)} size="panel" />
        <Stat label="VS" value={formatStat(player.vs)} size="panel" />
        <Stat
          label="Win rate"
          value={
            player.games_played > 0
              ? `${Math.round((player.games_won / player.games_played) * 100)}%`
              : "—"
          }
          size="panel"
        />
      </div>

      {!player.live ? (
        <p className="meta mt-4 opacity-55">
          TETR.IO could not be reached for this player, so these are the last
          numbers we stored.
        </p>
      ) : null}
    </section>
  );
}

export function Player() {
  const { username = "" } = useParams();
  const page = useApi<PlayerPage>(
    (signal) => playersApi.read(username, signal),
    [username],
  );

  if (page.loading) {
    return (
      <Shell>
        <p className="meta opacity-60">Loading…</p>
      </Shell>
    );
  }

  const data = page.data;
  // Only a 404 means "no such player". Reporting a 500 or a dropped connection
  // as "not on the roster" tells the visitor someone does not exist because the
  // server is unwell — and tells the club a member is missing when they are not.
  const isMissing = page.error instanceof ApiError && page.error.status === 404;
  if (page.error && !isMissing) {
    return (
      <Shell>
        <h1 className="mb-4 text-[clamp(2rem,5vw,3.25rem)]">
          That did not load
        </h1>
        <p className="panel max-w-[52ch] px-5 py-4 leading-relaxed">
          {page.error.message}{" "}
          <Link to="/#roster" className="underline underline-offset-4">
            Back to the roster
          </Link>
        </p>
      </Shell>
    );
  }
  if (!data) {
    return (
      <Shell>
        <h1 className="mb-4 text-[clamp(2rem,5vw,3.25rem)]">
          Not on the roster
        </h1>
        <p className="panel max-w-[52ch] px-5 py-4 leading-relaxed">
          Nobody by that name is in the club.{" "}
          <Link to="/#roster" className="underline underline-offset-4">
            Back to the roster
          </Link>
          .
        </p>
      </Shell>
    );
  }

  const { player, profile } = data;

  return (
    <Shell>
      <div className="flex flex-wrap items-start gap-8 md:gap-12">
        <Portrait page={data} />

        <div className="min-w-0 flex-1">
          <span className="meta block opacity-60">Tetris At UCI</span>
          <h1 className="text-[clamp(2.25rem,7vw,4rem)] leading-[0.95] normal-case">
            {player.display_name}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            {player.role ? (
              <span className="meta opacity-70">{player.role}</span>
            ) : null}
            {profile?.pronouns ? (
              <span className="meta opacity-70">{profile.pronouns}</span>
            ) : null}
            {profile?.favourite_piece ? (
              <span className="meta flex items-center gap-2 opacity-70">
                <Tetromino type={profile.favourite_piece} cell={8} />
                {profile.favourite_piece} piece
              </span>
            ) : null}
          </div>

          {profile?.bio ? (
            <p className="mt-6 max-w-[58ch] text-lg leading-relaxed whitespace-pre-line">
              {profile.bio}
            </p>
          ) : (
            <p className="mt-6 max-w-[58ch] leading-relaxed opacity-60">
              {profile
                ? "This player has not written anything yet."
                : "This player has not set up a profile. Their standings are below."}
            </p>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            {profile?.socials.map((link) => (
              <a
                key={`${link.label}-${link.url}`}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="btn btn--ghost px-4 py-2 text-xs"
              >
                {link.label}
              </a>
            ))}
            <a
              className="btn px-4 py-2 text-xs"
              href={`https://ch.tetr.io/u/${encodeURIComponent(player.username)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              ch.tetr.io ↗
            </a>
          </div>

          {profile ? (
            <p className="meta mt-8 opacity-50">
              Profile written by {profile.account}
            </p>
          ) : null}
        </div>
      </div>

      <Standings page={data} />

      <p className="mt-14">
        <Link to="/#roster" className="btn btn--ghost">
          ← Back to the roster
        </Link>
      </p>
    </Shell>
  );
}
